/**
 * Platform-aware modal hooks
 *
 * Wraps modal opening functions to show "feature blocked" modal
 * when running in extension sidebar
 */

import { useCallback, useState } from 'react';
import { usePlatform } from '../contexts/platform-context';
import { usePromptLibraryStore } from '../stores/prompt-library-store';

type BlockedFeature = {
  name: string;
  path: string;
} | null;

/**
 * Hook to manage blocked feature modal state
 */
export const useBlockedFeatureModal = () => {
  const [blockedFeature, setBlockedFeature] = useState<BlockedFeature>(null);

  const showBlockedFeature = useCallback((name: string, path: string) => {
    setBlockedFeature({ name, path });
  }, []);

  const clearBlockedFeature = useCallback(() => {
    setBlockedFeature(null);
  }, []);

  return {
    blockedFeature,
    showBlockedFeature,
    clearBlockedFeature,
  };
};

/**
 * Platform-aware prompt library modal opener
 * Shows blocked modal in sidebar, opens normally otherwise
 */
export const useOpenPromptLibrary = () => {
  const { isSidebar } = usePlatform();
  const openLibraryModal = usePromptLibraryStore(state => state.openLibraryModal);

  return useCallback(
    (onBlocked?: (name: string, path: string) => void) => {
      if (isSidebar) {
        // In sidebar - show blocked modal
        onBlocked?.('Prompt Library', '/settings?panel=prompts');
      } else {
        // Normal web app - open modal
        openLibraryModal();
      }
    },
    [isSidebar, openLibraryModal]
  );
};
